import { useState, useEffect, useRef, useCallback } from "react";
import { fetchSuggestions, fetchCalculation } from "./CommissionApi";

// ─── Theme ────────────────────────────────────────────────────────────────────
const B    = "#359cd7";
const BD   = "#1e7db8";
const BL   = "rgba(53,156,215,0.09)";
const BB   = "rgba(53,156,215,0.22)";
const GRAY = "#6b7280";
const INK  = "#111827";

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmt = (n) =>
  n == null || isNaN(n) || !isFinite(n)
    ? "—"
    : "$" + Number(Math.round(n)).toLocaleString("en-US");

const fmtPct = (n) =>
  n == null || isNaN(n) || !isFinite(n) ? "—" : Number(n).toFixed(1) + "%";

// ─── Debounce hook ─────────────────────────────────────────────────────────────
function useDebounce(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Editable Commission Card ──────────────────────────────────────────────────
function EditableCard({ icon, title, value, suggested, onChange, onReset, accent, isPercent, sub, note, loading }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState("");
  const inputRef              = useRef(null);

  const displayValue = value !== null ? value : suggested;
  const isOverridden = value !== null &&
    Math.round((value ?? 0) * 100) !== Math.round((suggested ?? 0) * 100);

  const startEdit = () => {
    setDraft(isPercent ? Number(displayValue).toFixed(1) : String(Math.round(displayValue ?? 0)));
    setEditing(true);
  };

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const commit = () => {
    const parsed = parseFloat(draft);
    if (!isNaN(parsed) && parsed >= 0) onChange(parsed);
    setEditing(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter") commit();
    if (e.key === "Escape") setEditing(false);
  };

  const displayVal = isPercent
    ? `${Number(displayValue ?? 0).toFixed(1)}%`
    : fmt(displayValue ?? 0);

  return (
    <div style={{
      flex: "1 1 120px", minWidth: 110, position: "relative",
      background: "#fff",
      border: `1px solid ${isOverridden ? accent : "#e5e7eb"}`,
      borderTop: `3px solid ${accent}`,
      borderRadius: 12, padding: "12px 10px",
      boxShadow: isOverridden ? `0 2px 12px ${accent}22` : "0 1px 4px rgba(0,0,0,0.05)",
      opacity: loading ? 0.55 : 1,
      transition: "box-shadow 0.2s, border-color 0.2s, opacity 0.2s",
    }}>
      {/* Edited badge */}
      {isOverridden && !loading && (
        <div style={{
          position: "absolute", top: 6, right: 6,
          fontSize: 10, fontWeight: 700, letterSpacing: "0.05em",  // CHANGE 1: 9→10
          textTransform: "uppercase", color: accent,
          background: `${accent}15`, padding: "2px 5px", borderRadius: 20,
          fontFamily: "'DM Sans',sans-serif",
        }}>Edited</div>
      )}
      {loading && (
        <div style={{ position: "absolute", top: 6, right: 6, fontSize: 10, color: "#9ca3af", fontFamily: "'DM Sans',sans-serif" }}>…</div>
      )}

      {/* CHANGE 1: icon 16→18, label 9→11, value 17→19 */}
      <div style={{ fontSize: 18, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, marginBottom: 4 }}>{title}</div>

      {editing ? (
        <div style={{ display: "flex", alignItems: "center", gap: 3, marginBottom: 2 }}>
          {!isPercent && <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: "#9ca3af" }}>$</span>}
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={handleKey}
            style={{
              width: 60, fontFamily: "'Lora',serif", fontSize: 19, fontWeight: 700,  // CHANGE 1: 16→19
              color: INK, border: "none", borderBottom: `2px solid ${accent}`,
              outline: "none", background: "transparent", padding: "0 2px",
            }}
          />
          {isPercent && <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: "#9ca3af" }}>%</span>}
        </div>
      ) : (
        <div
          onClick={startEdit}
          title="Click to edit"
          style={{
            fontFamily: "'Lora',serif", fontSize: 19, fontWeight: 700,  // CHANGE 1: 17→19
            color: isOverridden ? accent : INK, marginBottom: 2,
            cursor: "text", display: "inline-flex", alignItems: "center", gap: 4,
          }}
        >
          {displayVal}
          <span style={{ fontSize: 11, color: "#d1d5db", fontFamily: "'DM Sans',sans-serif" }}>✎</span>
        </div>
      )}

      {/* CHANGE 1: sub 10→12, note 9→11 */}
      {sub  && <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: "#9ca3af", marginBottom: note ? 2 : 0 }}>{sub}</div>}
      {note && <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: accent, marginTop: 2 }}>{note}</div>}

      {isOverridden && (
        <button
          onClick={onReset}
          style={{
            marginTop: 4, background: "none", border: "none", cursor: "pointer", padding: 0,
            fontFamily: "'DM Sans',sans-serif", fontSize: 10, color: "#9ca3af",  // CHANGE 1: 9→10
            textDecoration: "underline", display: "block",
          }}
        >
          ↺ Reset ({isPercent ? fmtPct(suggested) : fmt(suggested)})
        </button>
      )}
    </div>
  );
}

// ─── Static sub-components ────────────────────────────────────────────────────
function SliderField({ label, value, onChange, min, max, step = 1, prefix = "", suffix = "", hint }) {
  const pp = ((value - min) / (max - min)) * 100;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        {/* CHANGE 1: label 11→13 */}
        <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: GRAY }}>
          {label}
        </label>
        {/* CHANGE 1: value display 20→22 */}
        <span style={{ fontFamily: "'Lora',serif", fontSize: 22, fontWeight: 700, color: INK }}>
          {prefix}{value?.toLocaleString()}{suffix}
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          width: "100%", appearance: "none", WebkitAppearance: "none",
          height: 5, borderRadius: 3, outline: "none", cursor: "pointer",
          background: `linear-gradient(to right,${B} 0%,${B} ${pp}%,#e5e7eb ${pp}%,#e5e7eb 100%)`,
        }}
      />
      {/* CHANGE 1: hint 11→12 */}
      {hint && <p style={{ margin: "5px 0 0", fontSize: 12, color: "#9ca3af", fontFamily: "'DM Sans',sans-serif" }}>{hint}</p>}
    </div>
  );
}

function BRow({ label, amount, highlight, muted, tag, isDeduction, warning, dimmed }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "8px 12px", borderRadius: 8, marginBottom: 3,
      background: highlight ? (warning ? "rgba(239,68,68,0.07)" : BL) : muted ? "transparent" : dimmed ? "rgba(0,0,0,0.02)" : "#f9fafb",
      border: highlight ? `1px solid ${warning ? "rgba(239,68,68,0.25)" : BB}` : "1px solid transparent",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {/* CHANGE 1: row label 12→13 */}
        <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: muted ? "#9ca3af" : dimmed ? "#9ca3af" : "#374151" }}>{label}</span>
        {tag && (
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",  // CHANGE 1: 9→10
            color: warning ? "#dc2626" : BD,
            background: warning ? "rgba(239,68,68,0.1)" : BL,
            padding: "2px 6px", borderRadius: 20, fontFamily: "'DM Sans',sans-serif",
          }}>{tag}</span>
        )}
      </div>
      {/* CHANGE 1: row amount highlight 15→16, normal 12→13 */}
      <span style={{
        fontFamily: "'Lora',serif", fontSize: highlight ? 16 : 13, fontWeight: highlight ? 700 : 500,
        color: highlight ? (warning ? "#dc2626" : BD) : isDeduction ? "#dc2626" : muted ? "#9ca3af" : dimmed ? "#9ca3af" : INK,
      }}>{amount}</span>
    </div>
  );
}

function SectionLabel({ children }) {
  // CHANGE 1: 10→12
  return (
    <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#9ca3af", marginBottom: 10 }}>
      {children}
    </div>
  );
}

function Divider() {
  return <div style={{ height: 1, background: "#e5e7eb", margin: "8px 0" }} />;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CommissionCalculator() {
  const [price, setPrice]       = useState(2000);
  const [margin, setMargin]     = useState(40);
  const [convRate, setConvRate] = useState(20);
  const [showConv, setShowConv] = useState(false);

  const [demoOverride,     setDemoOverride]     = useState(null);
  const [salesPctOverride, setSalesPctOverride] = useState(null);
  const [guestOverride,    setGuestOverride]    = useState(null);
  const [noShowOverride,   setNoShowOverride]   = useState(null);

  const [suggested,  setSuggested]  = useState(null);
  const [breakdown,  setBreakdown]  = useState(null);
  const [resolved,   setResolved]   = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);

  const dPrice    = useDebounce(price);
  const dMargin   = useDebounce(margin);
  const dConvRate = useDebounce(convRate);

  useEffect(() => {
    setDemoOverride(null);
    setSalesPctOverride(null);
    setGuestOverride(null);
    setNoShowOverride(null);
  }, [dPrice, dMargin]);

  useEffect(() => {
    if (!dPrice || !dMargin) return;
    setLoading(true);
    setError(null);
    fetchCalculation({
      price:           dPrice,
      margin:          dMargin,
      convRate:        dConvRate,
      demoOverride,
      salesPctOverride,
      guestOverride,
      noShowOverride,
    })
      .then((data) => {
        setSuggested(data.suggested);
        setResolved(data.resolved);
        setBreakdown(data.breakdown);
      })
      .catch(() => setError("Cannot reach the backend. Make sure Django is running on port 8000."))
      .finally(() => setLoading(false));
  }, [dPrice, dMargin, dConvRate, demoOverride, salesPctOverride, guestOverride, noShowOverride]);

  const anyOverridden = demoOverride !== null || salesPctOverride !== null || guestOverride !== null || noShowOverride !== null;
  const resetAll = () => { setDemoOverride(null); setSalesPctOverride(null); setGuestOverride(null); setNoShowOverride(null); };

  const demo     = resolved?.demo_commission      ?? suggested?.demo_commission;
  const salesPct = resolved?.sales_commission_pct ?? suggested?.sales_commission_pct;
  const guest    = resolved?.guest_discount       ?? suggested?.guest_discount;
  const noShow   = resolved?.no_show_commission   ?? suggested?.no_show_commission;
  const hourly   = resolved?.hourly_rate          ?? suggested?.hourly_rate;

  // CHANGE 2: original margin amount derived from price + margin slider
  const originalMarginAmt = price * (margin / 100);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:wght@600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:18px;height:18px;border-radius:50%;background:${B};border:3px solid #fff;box-shadow:0 0 0 2px ${B},0 2px 8px rgba(53,156,215,0.25);cursor:pointer;transition:transform 0.15s}
        input[type=range]::-webkit-slider-thumb:hover{transform:scale(1.15)}
        input[type=range]::-moz-range-thumb{width:18px;height:18px;border-radius:50%;background:${B};border:3px solid #fff;cursor:pointer}
      `}</style>

      {/* ── Header ── */}
      <div style={{ padding: "20px 24px 0" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg,${B},#5cc0ed)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0, boxShadow: `0 4px 12px rgba(53,156,215,0.3)` }}>💡</div>
            {/* CHANGE 1: 10→12 */}
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: B }}>Commission Calculator</span>
          </div>
        </div>
        {/* CHANGE 1: description 12→13 */}
        <p style={{ margin: "0 0 16px", color: GRAY, fontSize: 13, lineHeight: 1.6, maxWidth: 480 }}>
          Enter your product details — we'll suggest commission amounts. Fine-tune any value and the breakdown updates live.
        </p>
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div style={{ margin: "0 24px 12px", background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8, padding: "8px 12px", fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: "#dc2626" }}>
          ⚠ {error}
        </div>
      )}

      {/* ── Salesperson callout ── */}
      <div style={{ margin: "0 24px 16px", background: BL, border: `1px solid ${BB}`, borderLeft: `4px solid ${B}`, borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "flex-start", gap: 10 }}>
        <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>🧑‍💼</span>
        <div>
          {/* CHANGE 1: quote 13→14 */}
          <p style={{ margin: "0 0 3px", fontFamily: "'Lora',serif", fontSize: 14, fontWeight: 700, color: BD }}>
            "Think of this like a salesperson — what would you pay?"
          </p>
          {/* CHANGE 1: body 11→12 */}
          <p style={{ margin: 0, fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: GRAY, lineHeight: 1.5 }}>
            A commissioned sales rep earns when they perform. Your video meeting Real customers work the same way — pay per meeting, reward conversions. Performance marketing with a human face.
          </p>
        </div>
      </div>

      {/* ── Sliders ── */}
      <div style={{ padding: "0 24px", marginBottom: 16 }}>
        <SliderField
          label="Average Product Price" value={price} onChange={setPrice}
          min={50} max={10000} step={50} prefix="$"
          hint={price < 300 ? "Lower price — flat demo fees work best" : price > 3000 ? "High-ticket — percentage commissions scale well" : "Mid-range — a mix of flat fee + % works best"}
        />
        <SliderField
          label="Your Gross Margin" value={margin} onChange={setMargin}
          min={5} max={80} step={1} suffix="%"
          hint={`${fmt(price * margin / 100)} gross margin per sale`}
        />
      </div>

      {/* ── Editable Commission Cards ── */}
      {suggested && (
        <div style={{ padding: "0 24px", marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <SectionLabel>Commission Structure — click any value to edit</SectionLabel>
            {anyOverridden && (
              <button
                onClick={resetAll}
                style={{ background: "none", border: `1px solid #e5e7eb`, borderRadius: 6, cursor: "pointer", padding: "3px 8px", fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: GRAY, fontWeight: 600, flexShrink: 0 }}
              >
                ↺ Reset all
              </button>
            )}
          </div>

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <EditableCard
              icon="🎯" title="Video meeting Commission" accent={B} isPercent={false} loading={loading}
              value={demoOverride}
              suggested={suggested.demo_commission}
              onChange={setDemoOverride}
              onReset={() => setDemoOverride(null)}
              sub="Per meeting"
              // CHANGE 3: reworded from "≈ $X/hr in total for host" → clearer phrasing
              note={`Real Customer earns ${fmt((demoOverride ?? suggested.demo_commission) * 6)}/hr (10-min call)`}
            />
            <EditableCard
              icon="🏆" title="Sales Commission" accent="#0ea5e9" isPercent={true} loading={loading}
              value={salesPctOverride}
              suggested={suggested.sales_commission_pct}
              onChange={setSalesPctOverride}
              onReset={() => setSalesPctOverride(null)}
              sub={`${fmt(price * (salesPctOverride ?? suggested.sales_commission_pct) / 100)} per sale`}
            />
            <EditableCard
              icon="🎁" title="Guest Discount" accent="#10b981" isPercent={false} loading={loading}
              value={guestOverride}
              suggested={suggested.guest_discount}
              onChange={setGuestOverride}
              onReset={() => setGuestOverride(null)}
              sub="Off for buyer"
            />
            <EditableCard
              icon="🚫" title="No-Show Fee" accent="#f59e0b" isPercent={false} loading={loading}
              value={noShowOverride}
              suggested={suggested.no_show_commission}
              onChange={setNoShowOverride}
              onReset={() => setNoShowOverride(null)}
              sub="Partial incentive"
            />
          </div>

          {anyOverridden && (
            // CHANGE 1: 11→12
            <p style={{ margin: "8px 0 0", fontSize: 12, color: B, fontFamily: "'DM Sans',sans-serif" }}>
              ✎ You've customised one or more values. Breakdown reflects your edits.
            </p>
          )}
        </div>
      )}

      {!suggested && !error && (
        // CHANGE 1: 12→13
        <div style={{ padding: "0 24px 16px", fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: "#9ca3af", textAlign: "center" }}>
          {loading ? "Fetching suggestions…" : "Adjust the sliders to get started."}
        </div>
      )}

      {/* ── Sale Breakdown ── */}
      {breakdown && (
        <div style={{ padding: "0 24px 24px" }}>
          <SectionLabel>Sale Breakdown</SectionLabel>
          <div
            style={{
              background: "#f9fafb",
              border: `1px solid ${anyOverridden ? BB : "#e5e7eb"}`,
              borderRadius: 12, padding: "16px 14px",
              opacity: loading ? 0.65 : 1,
              transition: "border-color 0.2s, opacity 0.2s",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              {/* CHANGE 1: 10→12 */}
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: anyOverridden ? B : "#9ca3af" }}>
                {anyOverridden ? "✎ Your Customised Numbers" : "🔄 Your Numbers (Dynamic)"}
              </span>
              {/* CHANGE 1: 10→11 */}
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: "#9ca3af" }}>
                {fmt(breakdown.original_price)} · {fmtPct(resolved?.sales_commission_pct ?? suggested?.sales_commission_pct)} commission
              </span>
            </div>

            <BRow label="Original Sale Price" amount={fmt(breakdown.original_price)} />

            {/* CHANGE 2: Original margin row — shown right after sale price, dimmed style */}
            <BRow
              label={`Your Gross Margin (${margin}%)`}
              amount={fmt(originalMarginAmt)}
              dimmed
              tag={`${margin}% of sale`}
            />

            <Divider />

            <BRow label="Real Customer Video meeting Payment" amount={`−${fmt(breakdown.demo_commission)}`} muted isDeduction />
            <BRow
              label={`Real Customer Sales Commission (${fmtPct(resolved?.sales_commission_pct ?? suggested?.sales_commission_pct)})`}
              amount={`−${fmt(breakdown.sales_commission)}`}
              muted isDeduction
            />
            <BRow label="Guest Discount" amount={`−${fmt(breakdown.guest_discount)}`} muted isDeduction />
            <BRow label="PureBrand Platform Fee (8%)" amount={`−${fmt(breakdown.purebrand_amt)}`} muted isDeduction />
            <Divider />
            <BRow
              label="Store's Remaining Margin"
              amount={breakdown.is_negative ? "⚠ Negative margin" : fmt(breakdown.per_sale_margin)}
              highlight
              warning={breakdown.is_negative}
              tag={breakdown.is_negative ? "Adjust values" : `${Number(breakdown.per_sale_margin_pct).toFixed(1)}% of margin`}
            />
          </div>
        </div>
      )}

      {/* ── Murphy Door reference ── */}
      <div style={{ padding: "0 24px 20px", marginTop: breakdown ? 0 : 8 }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "8px 12px", borderRadius: 8,
          background: BL, border: `1px solid ${BB}`,
        }}>
          <span style={{ fontSize: 13, flexShrink: 0 }}>📈</span>
          {/* CHANGE 1: 11→12 */}
          <p style={{ margin: 0, fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: BD, lineHeight: 1.5 }}>
            <strong style={{ fontWeight: 700 }}>Murphy Door reference:</strong>{" "}
            Murphy Door achieves a{" "}
            <strong style={{ fontWeight: 700, color: B }}>35% conversion rate</strong>{" "}
            using live video meetings — well above the 15–20% industry average. Use this as your benchmark when setting commission amounts.
            {suggested && (
              <>
                {" "}At 35%, your{" "}
                <strong style={{ fontWeight: 700, color: B }}>{fmt(price)}</strong>{" "}
                product would generate{" "}
                <strong style={{ fontWeight: 700, color: B }}>{fmt(price * 0.35)}</strong>{" "}
                expected revenue per video meeting booked.
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}